import { defineCollection } from "@rebasepro/common";
/**
 * Companies whose public application says what backend they run on.
 *
 * Signals are things people said; this is a thing a company *shipped*. Nothing
 * lands here on a suggestion: every row's domain was fetched, and `stack` is
 * what was actually found in the HTML or the script bundles, with the matched
 * string kept in `stack_evidence` so a reviewer can go and see it for
 * themselves.
 *
 * The board is a review queue like Signals: the agent writes into `new`, and a
 * human decides. Nothing downstream of `approved` happens automatically —
 * finding the person at a company is still a manual step, deliberately, because
 * a company row carries no author to resolve.
 */
/** A value as a trimmed string, or "" — display roles are text. */
const str = (value) => (typeof value === "string" ? value.trim() : value == null ? "" : String(value));
const companiesCollection = defineCollection({
    name: "Companies",
    singularName: "Company",
    slug: "companies",
    table: "companies",
    description: "Companies verified to be running a stack rebase replaces. The evidence is on every row — go and check it before you write to anyone.",
    history: true,
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        name: {
            name: "Name",
            type: "string",
            validation: { required: true }
        },
        domain: {
            name: "Domain",
            type: "string",
            validation: { required: true, unique: true },
            description: "Bare domain, lowercase. The dedup key — one row per company."
        },
        website: {
            name: "Website",
            type: "string",
            url: true,
            description: "Where the check actually landed, after redirects"
        },
        description: {
            name: "What they do",
            type: "string",
            admin: { multiline: true },
            description: "Taken from their own page, never from a model"
        },
        stack: {
            name: "Stack",
            type: "array",
            columnType: "text[]",
            of: { name: "Vendor", type: "string" },
            description: "Backend vendors detected on their site"
        },
        stack_evidence: {
            name: "Evidence",
            type: "string",
            admin: { markdown: true },
            description: "The literal strings that matched, and which document they were in"
        },
        frameworks: {
            name: "Frontend",
            type: "array",
            columnType: "text[]",
            of: { name: "Framework", type: "string" },
            description: "Context, not a buying signal"
        },
        fit_score: {
            name: "Fit",
            type: "number",
            validation: { min: 1, max: 5 },
            description: "5 = running a backend rebase directly replaces"
        },
        angle: {
            name: "Angle",
            type: "string",
            enum: [
                { id: "A", label: "A — RLS done right, admin included", color: "blue" },
                { id: "B", label: "B — Client-ready admin panel", color: "purple" },
                { id: "C", label: "C — Retool bill → zero", color: "green" }
            ]
        },
        why: {
            name: "Why they're here",
            type: "string",
            admin: { markdown: true },
            description: "What put this company on the list"
        },
        source: {
            name: "Found via",
            type: "string",
            validation: { required: true },
            defaultValue: "github_repo",
            enum: [
                { id: "github_repo", label: "GitHub repo", color: "gray" },
                { id: "signal", label: "A signal's link", color: "orange" },
                { id: "directory", label: "Model suggestion", color: "purple" },
                { id: "hiring", label: "Hiring post", color: "yellow" },
                { id: "manual", label: "Added by hand", color: "green" }
            ]
        },
        source_url: {
            name: "Source",
            type: "string",
            url: true
        },
        checked_at: {
            name: "Checked",
            type: "date",
            description: "When the site was last fetched. A stack goes stale — re-check before quoting it at anyone."
        },
        found_at: {
            name: "Found at",
            type: "date",
            autoValue: "on_create",
            admin: { readOnly: true }
        },
        status: {
            name: "Status",
            type: "string",
            validation: { required: true },
            defaultValue: "new",
            enum: [
                { id: "new", label: "New", color: "blue" },
                { id: "approved", label: "Worth approaching", color: "purple" },
                { id: "contacted", label: "Contacted", color: "green" },
                { id: "not_a_fit", label: "Not a fit", color: "gray" }
            ]
        },
        __order: {
            name: "Order",
            type: "string",
            admin: { disabled: true, hideFromCollection: true }
        }
    },
    admin: {
        icon: "Building2",
        group: "Outreach",
        defaultViewMode: "list",
        enabledViews: ["list", "table", "kanban"],
        kanban: {
            columnProperty: "status"
        },
        orderProperty: "__order",
        /**
         * The whole point of a company row is the stack it was caught running.
         * Without it the list is a column of domain names, and deciding which
         * one is worth an approach means opening every card to find the one
         * fact the lane exists to establish.
         */
        display: {
            title: "name",
            subtitle: ({ entity }) => str(entity.values.why) || str(entity.values.description) || str(entity.values.domain),
            status: "status",
            date: "found_at",
            tags: ({ entity }) => [
                str(entity.values.stack),
                entity.values.fit_score ? `fit ${entity.values.fit_score}` : "",
                ...(Array.isArray(entity.values.frameworks) ? entity.values.frameworks.slice(0, 2) : [])
            ].filter(Boolean)
        },
        sort: ["found_at", "desc"],
        propertiesOrder: [
            "name",
            "status",
            "fit_score",
            "stack",
            "domain",
            "website",
            "description",
            "angle",
            "why",
            "stack_evidence",
            "frameworks",
            "source",
            "source_url",
            "checked_at",
            "found_at",
            "__order"
        ],
        filterPresets: [
            {
                label: "On Supabase",
                filterValues: { stack: ["array-contains", "Supabase"] }
            },
            {
                label: "Strong fit (5)",
                filterValues: { fit_score: ["==", 5] }
            },
            {
                label: "New",
                filterValues: { status: ["==", "new"] }
            }
        ]
    }
});
export default companiesCollection;
